import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { formattedColorMaterial } from "../../../components/formatted/FormattedText";
import {
  buildObjectData,
  getFilteredAnomalies,
  mergeAndSumData,
  starsCalculation,
} from "../ActivityUtilities.js";
import { buildAnomalyChartFromDelivery } from "../utils/buildAnomalyChartData";

const getCollectionKey = (data, superContainerDataRow) => {
  if (superContainerDataRow) {
    const leftSide = superContainerDataRow.find((item) =>
      item._value_image?.includes("-L.jpeg"),
    );
    if (!leftSide?._measurement || !leftSide?._time) return null;
    return `${leftSide._measurement}_${leftSide._time}`;
  }
  if (!data?._measurement || !data?._time) return null;
  return `${data._measurement}_${data._time}`;
};

export const useActivityCollectionDetailData = ({
  data,
  superContainerDataRow,
}) => {
  const { t } = useTranslation();
  const API = process.env.REACT_APP_API_BACKEND;

  const collectionKey = useMemo(
    () => getCollectionKey(data, superContainerDataRow),
    [data, superContainerDataRow],
  );

  const collection = useSelector((state) =>
    collectionKey ? state.collection.data[collectionKey] : undefined,
  );

  const [anomalyDelivery, setAnomalyDelivery] = useState({});
  const [historyCycle, setHistoryCycle] = useState([]);
  const [dataSortedByAnomalies, setDataSortedByAnomalies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [historyId, setHistoryId] = useState(0);
  const [lastPackmatEfficiency, setLastPackmatEfficiency] = useState([]);
  const [numberOfAnomaly, setNumberOfAnomaly] = useState(0);
  const [numberOfStars, setNumberOfStars] = useState(3);
  const [numberOfPackmat, setNumberOfPackmat] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [csvDataReady, setCsvDataReady] = useState(false);
  const [totalVolumeSortingError, setTotalVolumeSortingError] = useState(0);
  const [totalWasteVolume, setTotalWasteVolume] = useState(0);
  const [colorObject, setColorObject] = useState([]);
  const [labelObjectSeparate, setLabelObjectSeparate] = useState([]);
  const [valueObjectSeparate, setValueObjectSeparate] = useState([]);
  const [dataNumberOfObject, setDataNumberOfObject] = useState([]);
  const [numberOfObjects, setNumberOfObjects] = useState(0);
  const [historyCyclePerFlow, setHistoryCyclePerFlow] = useState([]);
  // Super-container stats are always merged (no separate-view mode).
  const hasRenderedContentRef = useRef(false);

  const handleAnomalyList = useCallback(
    (typeAnomaly) => {
      if (typeAnomaly) {
        return Object.keys(typeAnomaly).map((type) => ({
          name: `${t(type)}${type === "sorting_errors" ? ` (${t("sum of the sorting errors chart salues")})` : ""}`,
          value: type,
          nameAndCount: `${t(type)} (${typeAnomaly[type]})`,
          color: formattedColorMaterial(type),
          count: typeAnomaly[type],
        }));
      }
      return undefined;
    },
    [t],
  );

  const prepareDataCSV = useCallback(
    (history) => {
      if (!data) return;

      const showLevel = !Array.isArray(history[0]);
      const label = [
        t("Date"),
        ...(showLevel ? [t("Filling level")] : []),
        t(data.weightUnit === "t" ? "Weight (tons)" : "Weight (kg)"),
      ];

      const normalizedHistory = Array.isArray(history[0])
        ? history.flat()
        : history;

      const lastData = normalizedHistory.map((item) => [
        moment(item._time).tz("Europe/Paris").format("DD MM YYYY HH:mm"),
        ...(showLevel ? [`${item.filling_level}%`] : []),
        showLevel
          ? data.weightUnit === "t"
            ? (item.mass / 1000).toFixed(2)
            : item.mass
          : "",
      ]);
      const csvContent = [label, ...lastData];

      setCsvData(csvContent);
      setCsvDataReady(true);
    },
    [data, t],
  );

  useEffect(() => {
    if (!data || !collectionKey) return;

    if (!collection) {
      setIsLoading(true);
      setCsvDataReady(false);
      return;
    }

    const isSuperContainer = Boolean(superContainerDataRow);
    // Keep the page interactive: only show the spinner on the first paint.
    if (!hasRenderedContentRef.current) {
      setIsLoading(true);
    }

    const history = isSuperContainer
      ? collection[0].historyCurrent
      : collection.historyCurrent;
    const filteredAndSorted = getFilteredAnomalies(history);

    setHistoryCycle(history);
    setHistoryId(
      isSuperContainer && superContainerDataRow?.[0]?.showDepotCycle
        ? history.length - 1
        : 0,
    );
    setDataSortedByAnomalies(filteredAndSorted);

    setNumberOfPackmat(
      isSuperContainer
        ? collection[0].numberOfPackmat
        : collection.numberOfPackmat,
    );
    setLastPackmatEfficiency(
      isSuperContainer
        ? collection[0].lastPackmatEfficiency
        : collection.lastPackmatEfficiency,
    );
    setNumberOfAnomaly(
      isSuperContainer
        ? collection.map((c) => c.numberOfAnomaly)
        : [collection.numberOfAnomaly],
    );

    let mergedVolumeSortingError = 0;
    let mergedTotalWasteVolume = 0;
    let stars = 0;

    if (isSuperContainer) {
      mergedVolumeSortingError = collection.reduce(
        (acc, item) => acc + (item.totalVolumeSortingError || 0),
        0,
      );
      mergedTotalWasteVolume = collection.reduce(
        (acc, item) => acc + (item.totalWasteVolume || 0),
        0,
      );
      stars = starsCalculation(
        mergedVolumeSortingError,
        mergedTotalWasteVolume,
      );
    } else {
      mergedVolumeSortingError = collection.totalVolumeSortingError;
      mergedTotalWasteVolume = collection.totalWasteVolume;
      stars = collection.numberOfStars;
    }
    setNumberOfStars(stars);
    setTotalVolumeSortingError(mergedVolumeSortingError);
    setTotalWasteVolume(mergedTotalWasteVolume);

    if (isSuperContainer) {
      const mergedCollection = mergeAndSumData(
        Array.isArray(collection) ? collection : [collection],
      );

      const { objectColors, objectNames, objectValues, objectCount } =
        buildObjectData(mergedCollection.objectDelivery, handleAnomalyList);
      const objectsTotal = objectCount.reduce(
        (sum, count) => sum + (Number(count) || 0),
        0,
      );

      setNumberOfObjects([
        objectsTotal > 0 ? objectsTotal : mergedCollection.numberOfObjects,
      ]);
      setAnomalyDelivery(mergedCollection.anomalyDelivery);
      setNumberOfAnomaly([mergedCollection.numberOfAnomaly]);
      setColorObject([objectColors]);
      setLabelObjectSeparate([objectNames]);
      setValueObjectSeparate([objectValues]);
      setDataNumberOfObject([objectCount]);
      // L+R histories for object zoom / material bilan (same as characterization fusion).
      setHistoryCyclePerFlow([
        Array.isArray(collection)
          ? collection.flatMap((item) => item.historyCurrent || [])
          : mergedCollection.historyCurrent,
      ]);
    } else {
      setNumberOfObjects([collection.numberOfObjects]);
      setAnomalyDelivery(collection.anomalyDelivery);

      const { objectColors, objectNames, objectValues, objectCount } =
        buildObjectData(collection.objectDelivery, handleAnomalyList);
      setColorObject([objectColors]);
      setLabelObjectSeparate([objectNames]);
      setValueObjectSeparate([objectValues]);
      setDataNumberOfObject([objectCount]);
      setHistoryCyclePerFlow([collection.historyCurrent]);
    }

    const histories = isSuperContainer
      ? collection.map((c) => c.historyCurrent)
      : collection.historyCurrent;
    prepareDataCSV(histories);

    hasRenderedContentRef.current = true;
    setIsLoading(false);
  }, [
    collection,
    collectionKey,
    data,
    handleAnomalyList,
    prepareDataCSV,
    superContainerDataRow,
  ]);

  const buildChartArrays = useCallback(
    (delivery, anomalyCount) => {
      const chart = buildAnomalyChartFromDelivery(
        delivery,
        anomalyCount,
        t,
        formattedColorMaterial,
      );
      return {
        values: chart.values,
        labels: chart.labels,
        colors: chart.colors,
        keys: chart.keys,
        chartTotal: chart.chartTotal,
      };
    },
    [t],
  );

  const {
    dataAnomalyDelivery,
    labelsAnomalyDelivery,
    colorAnomalyDelivery,
    valueAnomalyDelivery,
    anomalyChartTotals,
  } = useMemo(() => {
    if (superContainerDataRow) {
      const anomalyCount = Array.isArray(numberOfAnomaly)
        ? numberOfAnomaly[0]
        : numberOfAnomaly;
      const chart = buildChartArrays(anomalyDelivery, anomalyCount);
      return {
        dataAnomalyDelivery: [chart.values],
        labelsAnomalyDelivery: [chart.labels],
        colorAnomalyDelivery: [chart.colors],
        valueAnomalyDelivery: [chart.keys],
        anomalyChartTotals: [chart.chartTotal],
      };
    }

    const anomalyCount = Array.isArray(numberOfAnomaly)
      ? numberOfAnomaly[0]
      : numberOfAnomaly;
    const chart = buildChartArrays(anomalyDelivery, anomalyCount);
    return {
      dataAnomalyDelivery: [chart.values],
      labelsAnomalyDelivery: [chart.labels],
      colorAnomalyDelivery: [chart.colors],
      valueAnomalyDelivery: [chart.keys],
      anomalyChartTotals: [chart.chartTotal],
    };
  }, [
    anomalyDelivery,
    buildChartArrays,
    numberOfAnomaly,
    superContainerDataRow,
  ]);

  const findHistoryId = useCallback(
    (targetTime) => {
      const index = historyCycle.findIndex((item) => item._time === targetTime);
      setHistoryId(index);
    },
    [historyCycle],
  );

  return {
    API,
    isLoading,
    csvData,
    csvDataReady,
    dataSortedByAnomalies,
    historyId,
    setHistoryId,
    lastPackmatEfficiency,
    numberOfStars,
    numberOfPackmat,
    totalVolumeSortingError,
    totalWasteVolume,
    colorObject,
    labelObjectSeparate,
    valueObjectSeparate,
    dataNumberOfObject,
    numberOfObjects,
    dataAnomalyDelivery,
    labelsAnomalyDelivery,
    colorAnomalyDelivery,
    valueAnomalyDelivery,
    anomalyChartTotals,
    historyCycle,
    historyCyclePerFlow,
    numberOfAnomaly,
    findHistoryId,
  };
};
